/**
 * Identifica se a requisição atual pertence à experiência da marca Vivame.
 *
 * As PLPs da Vivame são resolvidas no CMS (variante de seção na página
 * `Category`, ver `.deco/blocks/pages-category-*.json`) porque o pathname já
 * diz tudo. A PDP (`/:slug/p`) não: a URL só tem o slug do produto, então a
 * marca precisa vir do catálogo — é o que `isVivameProduct` faz.
 */
import { createCachedLoader } from "@decocms/blocks/sdk/cachedLoader";
import { getVtexConfig } from "@decocms/apps-vtex";

/** Raízes de categoria que compõem o catálogo da Vivame. */
const VIVAME_CATEGORY_PREFIXES = ["/sport/", "/outlet/feminino/fitness/"];

interface CatalogProduct {
  categories?: string[];
}

const fetchProductCategories = createCachedLoader(
  "site/vivame/productCategories",
  async ({ slug }: { slug: string }): Promise<string[]> => {
    const { account } = getVtexConfig();
    const url =
      `https://${account}.vtexcommercestable.com.br` +
      `/api/catalog_system/pub/products/search/${encodeURIComponent(slug)}/p`;

    const response = await fetch(url, { headers: { accept: "application/json" } });
    if (!response.ok) return [];

    const products = (await response.json()) as CatalogProduct[];
    return products?.[0]?.categories ?? [];
  },
  "product",
);

/** `/calca-legging-x/p` → `calca-legging-x`; qualquer outra rota → `null`. */
export function pdpSlugFromPathname(pathname: string): string | null {
  const match = /^\/([^/]+)\/p\/?$/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : null;
}

export function isVivameCategoryPath(categories: string[]): boolean {
  return categories.some((category) => {
    const normalized = category.toLowerCase();
    return VIVAME_CATEGORY_PREFIXES.some((prefix) => normalized.startsWith(prefix));
  });
}

/**
 * Consulta o catálogo da VTEX para saber se o produto da PDP é da Vivame.
 * Qualquer falha responde `false` — na dúvida, mantém o header da DT.
 */
export async function isVivameProduct(slug: string): Promise<boolean> {
  try {
    return isVivameCategoryPath(await fetchProductCategories({ slug }));
  } catch {
    return false;
  }
}
